import classnames from 'classnames';
import Immutable from 'immutable';
import * as React from 'react';
import Spinner from 'components/Spinner';
import { SpinnerSize } from 'components/Spinner/models';
import { ProcessType } from 'const';
import { LoadingOverlayOptionsMap } from 'models';
import { intlGet } from 'utils/intlGet';
import styles from './styles.module.scss';

type LoadingTitle = {
  title: () => string;
  info: () => string;
};

export const ProcessTypeLoadingText: PartialRecord<ProcessType, LoadingTitle> = {
  [ProcessType.REFRESH]: {
    title: () => intlGet('Loading', 'Refreshing'),
    info: () => intlGet('Loading', 'ProcessInformation'),
  },
  [ProcessType.DUPLICATES_CHECKING]: {
    title: () => intlGet('Loading', 'DuplicatesChecking'),
    info: () => intlGet('Loading', 'ProcessInformation'),
  },
  [ProcessType.EXPORTING_FILES]: {
    title: () => intlGet('Loading', 'ExportingFiles'),
    info: () => intlGet('Loading', 'ProcessInformation'),
  },
  [ProcessType.UPLOAD_TO_PROMOMATS]: {
    title: () => intlGet('Loading', 'UploadingFilesToPromoMats'),
    info: () => intlGet('Loading', 'ProcessInformation'),
  },
  [ProcessType.IMPORT_TRANSLATION_PACKAGE]: {
    title: () => intlGet('Loading', 'ImportTranslationPackage'),
    info: () => intlGet('Loading', 'ProcessInformation'),
  },
  [ProcessType.SAVE_GROUPED_REUSABLE_LAYOUT]: {
    title: () => '',
    info: () => intlGet('Loading', 'SaveGroupedReusableLayout'),
  },
  [ProcessType.SAVE_REUSABLE_LAYOUT]: {
    title: () => '',
    info: () => intlGet('Loading', 'SaveReusableLayout'),
  },
  [ProcessType.AI_TRANSLATION]: {
    title: () => intlGet('Loading', 'AITranslation'),
    info: () => intlGet('Loading', 'ProcessInformation'),
  },
};

function getClassNameModifier(activeProcessType: ProcessType): string {
  switch (activeProcessType) {
    case ProcessType.SAVE_LAYOUT_WITHOUT_MESSAGE:
      return 'save-reusable-layout-without-message';
    case ProcessType.SAVE_REUSABLE_LAYOUT:
    case ProcessType.SAVE_GROUPED_REUSABLE_LAYOUT:
      return 'save-reusable-layout';
    case ProcessType.DOCUMENT_UPLOADING:
      return 'document-uploading';
    case ProcessType.REFRESH:
    case ProcessType.DUPLICATES_CHECKING:
    case ProcessType.EXPORTING_FILES:
    case ProcessType.UPLOAD_TO_PROMOMATS:
    case ProcessType.IMPORT_TRANSLATION_PACKAGE:
    case ProcessType.AI_TRANSLATION:
      return 'project';
    case ProcessType.GET_PROJECT_DATA:
      return 'welcome-screen';
    default:
      return 'empty';
  }
}

type Props = {
  isLocked: boolean;
  activeProcessType: ProcessType;
  options?: LoadingOverlayOptionsMap;
  spinnerSize?: SpinnerSize;
  dataTestId?: string;
};

const LoadingOverlay: React.FC<Props> = ({
  isLocked,
  activeProcessType,
  options = Immutable.Map() as LoadingOverlayOptionsMap,
  spinnerSize = 'lg',
  dataTestId = 'loadingOverlay',
}) => {

  if (!isLocked) {
    return null;
  }

  const classNameModifier = getClassNameModifier(activeProcessType);
  const processText = ProcessTypeLoadingText[activeProcessType];
  const title = options?.get('title') || processText?.title();
  const info = options?.get('info') || processText?.info();
  const isAITranslation = activeProcessType === ProcessType.AI_TRANSLATION;
  const subInfo = isAITranslation ? options?.get('info') : undefined;

  return (
    <div className={classnames(styles.overlay, styles[`overlay_${classNameModifier}`])}
      data-testid={dataTestId}>
      <div className={classnames(styles.overlayWindow, styles[`overlayWindow_${classNameModifier}`])}>
        <span className={styles.title} data-testid={`${dataTestId}-title`}>{title}</span>
        <Spinner size={spinnerSize} data-testid={`${dataTestId}-spinner`} />
        {isAITranslation ? (
          <>
            <span className={styles.subInfo} data-testid={`${dataTestId}-sub-info`}>
              {subInfo}
            </span>
            <span className={styles.doNotClose} data-testid={`${dataTestId}-do-not-close`}>
              {intlGet('Loading', 'DoNotCloseWindow')}
            </span>
          </>
        ) : (
          <span className={classnames(styles.info, styles[`info_${classNameModifier}`])}
            data-testid={`${dataTestId}-info`}>
            {info}
          </span>
        )}
      </div>
    </div>
  );
};

export default LoadingOverlay;
